import type { TileSet } from './tiles';
import { pairs, type WorldMapSpec } from './worldSpec';

/** Greendale Kingdom's painting (approved 26 October), tiled as
 *  public/world/kingdom/v1 at WebP Q74. A changed painting goes in a new
 *  version folder, with its spots measured again. */
export const KINGDOM_TILES: TileSet = {
  base: '/world/kingdom/v1/map_files',
  width: 3072,
  height: 2048,
  tileSize: 512,
  top: 3,
};

/**
 * Greendale Kingdom, from the painting's spots.json. Four buildings stand
 * close to the road (less than 140 px of clear ground below their middles:
 * Forest Reels, Castle Gardens, the Wizard's Tower and Dragon's Hoard), so
 * their plates hang above them or beside them instead of on the road
 * (Jon, 26 October); the rest hang below, as spots.json has them.
 */
export const KINGDOM: WorldMapSpec = {
  biome: 'kingdom',
  tiles: KINGDOM_TILES,
  buildings: [
    {
      node: 1,
      name: 'Forest Reels',
      centre: [345, 870],
      plate: [345, 570],
      plateTight: [345, 550],
      outline: pairs([
        140, 1000, 130, 900, 170, 800, 240, 720, 330, 690, 430, 700, 510, 750, 555, 830, 565, 930,
        540, 1010, 450, 1040, 250, 1040,
      ]),
    },
    {
      node: 2,
      name: 'Royal Reels',
      centre: [920, 540],
      plate: [1012, 826],
      plateTight: [880, 1020],
      outline: pairs([
        695, 720, 695, 560, 720, 470, 760, 410, 830, 330, 880, 280, 940, 250, 1010, 240, 1060, 300,
        1080, 400, 1130, 420, 1150, 560, 1145, 720, 1060, 760, 1010, 820, 920, 820, 860, 760, 760,
        740,
      ]),
    },
    {
      node: 3,
      name: 'Castle Gardens',
      centre: [1555, 660],
      plate: [1555, 240],
      plateTight: [1555, 240],
      outline: pairs([
        1240, 700, 1270, 600, 1340, 540, 1420, 520, 1480, 470, 1560, 410, 1650, 470, 1700, 490,
        1730, 470, 1760, 520, 1860, 610, 1870, 740, 1800, 830, 1700, 890, 1620, 905, 1500, 905,
        1400, 880, 1300, 830, 1240, 770,
      ]),
    },
    {
      node: 4,
      name: "Knights' Quest",
      centre: [1545, 1210],
      plate: [1526, 1478],
      plateTight: [1580, 1478],
      outline: pairs([
        1150, 1100, 1180, 1000, 1240, 920, 1300, 960, 1400, 975, 1520, 1060, 1600, 1040, 1690, 1000,
        1760, 1060, 1800, 1110, 1840, 1120, 1900, 1180, 1940, 1300, 1930, 1380, 1880, 1430, 1700,
        1460, 1500, 1470, 1380, 1450, 1300, 1420, 1190, 1400, 1150, 1250,
      ]),
    },
    {
      node: 5,
      name: "Wizard's Tower",
      centre: [2085, 590],
      plate: [2085, 160],
      plateTight: [2425, 190],
      outline: pairs([
        1915, 760, 1920, 540, 1950, 520, 1990, 470, 2010, 330, 2060, 290, 2130, 280, 2160, 330, 2190,
        440, 2200, 520, 2190, 580, 2300, 560, 2310, 780, 2240, 820, 2200, 880, 2110, 890, 2000, 860,
        1950, 830,
      ]),
    },
    {
      node: 6,
      name: 'Castle Dungeons',
      centre: [2470, 760],
      plate: [2424, 985],
      plateTight: [2370, 1049],
      outline: pairs([
        2290, 900, 2300, 700, 2360, 600, 2440, 540, 2540, 530, 2600, 560, 2650, 650, 2660, 800, 2620,
        900, 2600, 970, 2380, 980, 2310, 960,
      ]),
    },
    {
      node: 7,
      name: "Dragon's Hoard",
      centre: [2820, 900],
      plate: [2850, 520],
      plateTight: [2796, 380],
      outline: pairs([
        2640, 1110, 2645, 980, 2680, 860, 2740, 760, 2800, 700, 2860, 665, 2940, 675, 3020, 740,
        3072, 800, 3072, 1160, 2980, 1190, 2800, 1190, 2680, 1170,
      ]),
    },
  ],
  road: pairs([
    0, 1033, 128, 1077, 256, 1136, 384, 1153, 512, 1127, 640, 1115, 768, 1087, 896, 1037, 1024, 972,
    1152, 910, 1280, 900, 1408, 937, 1536, 964, 1664, 964, 1792, 945, 1920, 962, 2048, 1006, 2176,
    1014, 2304, 1086, 2432, 1168, 2560, 1211, 2688, 1239, 2816, 1266, 2944, 1259, 3072, 1236,
  ]),
  roadWidth: 80,
  still: [
    {
      name: 'bridge',
      outline: pairs([
        860, 1612, 860, 1545, 882, 1517, 933, 1514, 933, 1481, 977, 1479, 980, 1489, 1018, 1467, 1058,
        1457, 1098, 1455, 1099, 1423, 1147, 1423, 1149, 1479, 1207, 1485, 1255, 1487, 1257, 1554,
        1295, 1561, 1310, 1578, 1312, 1632, 1228, 1630, 1228, 1620, 1205, 1608, 1186, 1603, 1166,
        1607, 1154, 1618, 1150, 1638, 1146, 1662, 1128, 1682, 1040, 1682, 1030, 1662, 1020, 1630,
        1002, 1612, 976, 1609, 945, 1611, 920, 1615, 905, 1618,
      ]),
    },
    {
      name: 'path from the bridge to the lists',
      outline: pairs([1100, 1430, 1150, 1380, 1230, 1370, 1260, 1420, 1200, 1460, 1130, 1460]),
    },
    // Painted props nobody may stand on or in front of (a pennant cannot
    // carry a person): the striped tournament tent and its flag, and the
    // lists' flag pole.
    {
      name: 'red tournament tent and its flag',
      outline: pairs([
        1243, 905, 1256, 905, 1258, 915, 1308, 926, 1308, 946, 1258, 950, 1260, 972, 1312, 1030,
        1320, 1060, 1322, 1090, 1178, 1090, 1180, 1075, 1215, 1030, 1243, 972,
      ]),
    },
    {
      name: 'tournament flag pole (top)',
      outline: pairs([
        1400, 962, 1412, 962, 1414, 972, 1470, 980, 1470, 1002, 1414, 1004, 1412, 1012, 1400, 1012,
      ]),
    },
  ],
  // The painting has no lamps of its own. The posts the code draws are the
  // road's computed spacing (a post every 170 px along it, on alternate
  // verges), kept where they were except five that stood on plates (726,1039),
  // (923,1086), (1025,909), (2178,1079) and (2382,1069): those stand on open
  // grass beside the road instead (a 2x crop at every foot, 30 October): off
  // the water, foliage, buildings, plates and still areas.
  lanternPosts: pairs([
    99, 1008, 213, 1178, 392, 1094, 576, 1177, 738, 1010, 873, 992, 1068, 1007, 1540, 1020, 1701,
    902, 1870, 1012, 2058, 950, 2240, 975, 2352, 1200, 2485, 1245, 2679, 1180, 2837, 1321, 2993,
    1193,
  ]),
  plateBox: [420, 110],
  plateBoxTight: [528, 138],
  ambient: '/world/kingdom/ambient/v3',
  ambientFiles: ['life.json', 'life.webp', 'sway.webp', 'water.webp'],
  // Willow is a deer and Sir Clank a knight: the Kingdom's life has a goat,
  // riderless horses and a herald instead.
  forbidden: ['deer', 'fawn', 'reindeer', 'knight'],
};
